import { cloneDeep } from 'lodash-es';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { CustomLeagueItem } from '@/models/customLeaguePage';
import { createCustomLeaguesPageHeaders } from '@/pages/system/customLeagues/CustomLeaguesPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { customLeagueService } from '@/services/customLeagueService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { usePersistentFilters } from '@/stores/composables/usePersistentFilters';
import { IPageOptions } from '@/types';
import type { ICustomLeagueFilters, ICustomLeagueUpdateResponse } from '@/types/customLeaguePage';
import type { DataTableHeader } from '@/types/vuetify';

function createCustomLeagueStoreSetup(storeId: string) {
    const items = shallowRef<CustomLeagueItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    const filters = reactive<ICustomLeagueFilters>({
        sportId: createRequiredSportFilter(),
        customLeagueName: {
            value: null,
            title: 'customLeagues.name',
            allowSaveState: true,
        },
        customLeagueGender: {
            value: null,
            title: 'customLeagues.gender',
            allowSaveState: true,
        },
    });

    const hiddenFilters = ref<string[]>([]);
    const visibleFiltersMobile = ref<string[]>([]);
    const filterOrder = ref<string[]>(['sportId', 'customLeagueName', 'customLeagueGender']);

    const availableFilters = computed(() => {
        const entries = Object.entries(filters).map(([key, meta]) => ({
            key,
            title: meta.title || '',
        }));

        if (filterOrder.value.length > 0) {
            entries.sort((a, b) => {
                const indexA = filterOrder.value.indexOf(a.key);
                const indexB = filterOrder.value.indexOf(b.key);
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });
        }

        return entries;
    });

    const useButtonForFiltersApply = ref(false);
    const saveFiltersState = ref(false);

    const visibleFilters = computed(() => Object.keys(filters).filter((key) => !hiddenFilters.value.includes(key)));

    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [{ key: 'name', order: 'asc' }],
    });

    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.customLeagueName.value = null;
        filters.customLeagueGender.value = null;
    }

    const headers = computed<DataTableHeader[]>(() => createCustomLeaguesPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    const persistentFilters = usePersistentFilters({
        filters,
        enabled: computed(() => saveFiltersState.value && storeId === 'customLeagues'),
    });

    const reloadFiltersFromStorage = () => {
        resetFilters();
        if (storeId === 'customLeagues' && saveFiltersState.value) {
            persistentFilters.loadFromStorage();
        }
    };

    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country', 'AuditTable'],
            };

            const response = await customLeagueService.list(params);

            items.value = (response.data.rows || []).map((row) => new CustomLeagueItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function mergeMutationResponse(response: { data: ICustomLeagueUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;

        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            items.value[idx] = new CustomLeagueItem(row);
        } else {
            items.value = [new CustomLeagueItem(row), ...items.value];
            total.value += 1;
        }
        triggerRef(items);
    }

    async function patchBoolField(item: CustomLeagueItem, modelKey: 'fixing', value: 0 | 1, apiField: string) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await customLeagueService.update({ rows: [{ id: item.id, [apiField]: value }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch {
            item[modelKey] = oldValue;
            triggerRef(items);
        }
    }

    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        patchBoolField,
        mergeMutationResponse,
        hasAdd: true,
    };
}

export function useCustomLeagueStore(storeId: string) {
    const useStore = defineStore(storeId, () => createCustomLeagueStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'customLeagues'),
    });
    return useStore();
}

export type CustomLeagueStoreSetup = ReturnType<typeof useCustomLeagueStore>;
